import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  HypothesesConclusionNotice as ConclusionNotice,
  HypothesesConclusionNoticeSkip as ConclusionNoticeSkip,
  HypothesesHypothesisEvidenceItem as HypothesisEvidenceItem,
  HypothesesHypothesisEvidenceList as HypothesisEvidenceList,
  HypothesesSubmissionEvidenceLink as SubmissionEvidenceLink,
} from '../generated/api/types.gen.ts';
import { hypothesesAddEvidence, hypothesesListEvidence, hypothesesRemoveEvidence } from '../generated/api/sdk.gen.ts';
import { ApiFeil } from './feil.ts';
import { useApiKlient } from './klient.ts';
import { behandlingNoekkel } from './useHypoteseBehandling.ts';
import { hypoteseNoekkel } from './useHypotheses.ts';
import { sakNoekkel } from './useSubmissions.ts';

export type { ConclusionNotice, ConclusionNoticeSkip, HypothesisEvidenceItem, HypothesisEvidenceList, SubmissionEvidenceLink };

/**
 * H3.1 — saker som grunnlag for en hypotese, og notisen tilbake til sluttbrukerne bak dem.
 *
 * Grunnlaget leser saker, og har derfor sitt eget kall (`GET /hypotheses/{id}/evidence`) i
 * stedet for å ligge i detaljen: hypotesen vises med én gang, og lista får sitt eget skjelett.
 * Tallene (`count`, `reach`, `anonymousCount`) får alle som kan lese hypotesen; sakene (`items`)
 * bare den som har en rolle i Innspill — en bidragsyter får aldri en sakstekst (Kh7).
 *
 * Nøkkelen eies her og bare her (`react-ts-integration`, «Query-nøkler har én eier»). Den deler
 * prefikset `hypotheses`, så en overgang som friskere opp hele rommet, friskere også grunnlaget.
 */
export const grunnlagNoekkel = {
  liste: (id: string) => ['hypotheses', 'grunnlag', id] as const,
};

function kast(res: { response?: Response; data?: unknown; error?: unknown }): never {
  throw ApiFeil.fra(res.response?.status ?? 0, res.error ?? res.data);
}

/** Grunnlaget for én hypotese. Skriver ingen logg-rad hos tjeneren (Kh9). */
export function useHypoteseGrunnlag(id: string | null, enabled = true) {
  const klient = useApiKlient();
  return useQuery({
    queryKey: grunnlagNoekkel.liste(id ?? ''),
    enabled: enabled && id !== null,
    queryFn: async (): Promise<HypothesisEvidenceList> => {
      const res = await hypothesesListEvidence({ client: klient, path: { id: id! } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as HypothesisEvidenceList;
    },
  });
}

/**
 * Etter en lenking, en fjerning eller en notis er alt som teller grunnlaget utdatert: lista,
 * hypotesens tall (`evidenceCount`, `evidenceReach`), merket «notis til brukerne» i lista og
 * «Til deg», og sakens «Grunnlag for H-142».
 */
function friskOppGrunnlag(queryClient: ReturnType<typeof useQueryClient>, hypoteseId: string, sakId?: string) {
  void queryClient.invalidateQueries({ queryKey: grunnlagNoekkel.liste(hypoteseId) });
  void queryClient.invalidateQueries({ queryKey: hypoteseNoekkel.detalj(hypoteseId) });
  void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'nummer'] });
  void queryClient.invalidateQueries({ queryKey: ['hypotheses', 'liste'] });
  void queryClient.invalidateQueries({ queryKey: behandlingNoekkel.counts });
  if (sakId) void queryClient.invalidateQueries({ queryKey: sakNoekkel.detalj(sakId) });
}

/**
 * Lenker en sak til hypotesen (`POST /hypotheses/{id}/evidence`) — samme kall fra saken og fra
 * hypotesen. Idempotent: en sak som alt er lenket gir den lenken som finnes. Lenkingen endrer
 * ingenting på saken; den kalles straks, og «Fjern» er veien tilbake.
 */
export function useLenkGrunnlag() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ hypoteseId, sakId }: { hypoteseId: string; sakId: string }): Promise<HypothesisEvidenceItem> => {
      const res = await hypothesesAddEvidence({ client: klient, path: { id: hypoteseId }, body: { submissionId: sakId } });
      if (!res.response?.ok || !res.data) kast(res);
      return res.data as HypothesisEvidenceItem;
    },
    onSuccess: (_item, { hypoteseId, sakId }) => friskOppGrunnlag(queryClient, hypoteseId, sakId),
  });
}

/**
 * Tar en sak ut av grunnlaget (`DELETE /hypotheses/{id}/evidence/{submissionId}`). Klienten
 * holder kallet tilbake i angre-vinduet; det finnes ikke noe angre-endepunkt.
 */
export function useFjernGrunnlag() {
  const klient = useApiKlient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ hypoteseId, sakId }: { hypoteseId: string; sakId: string }): Promise<void> => {
      const res = await hypothesesRemoveEvidence({ client: klient, path: { id: hypoteseId, submissionId: sakId } });
      if (!res.response?.ok) kast(res);
    },
    onSuccess: (_v, { hypoteseId, sakId }) => friskOppGrunnlag(queryClient, hypoteseId, sakId),
  });
}
